import { useTranslation } from 'react-i18next';
import type { ExamResultItem } from '@proref/types';
import { Icon } from '../shared/Icons';
import { VideoPlayer } from '../shared/VideoPlayer';

// The graded card for a single exam question, shared by the candidate's own results page and the
// admin results console. Both need the identical four-state option rendering — extracted here the
// moment the second caller appeared, rather than letting the two drift apart.
const OPTION_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

// One reviewed question — the options with the taker's picks + the revealed correct answers.
// Four states per option, which is what makes a multi-answer question readable at a glance:
// picked-and-right, missed-but-right, picked-and-wrong, and untouched.
interface ExamResultItemCardProps {
  item: ExamResultItem;
  index: number;
  // Whose sitting this is. The candidate reads "Your answer"; an admin reading someone else's
  // sitting in the results console needs the same card to say "Their answer" — the only thing
  // that differs between the two views, so it's a prop rather than a second component.
  voice?: 'self' | 'candidate';
}

export function ExamResultItemCard({ item, index, voice = 'self' }: ExamResultItemCardProps) {
  const { t } = useTranslation();
  // A partial hit is neither "correct" nor a plain miss — it earned points without earning the
  // question, and hiding that would make the score look wrong against the per-question badges.
  const partial = item.is_correct === false && item.points > 0;
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[12px] font-medium text-muted">{t('exams.resultItem.questionN', { number: index + 1 })}</div>
        <div className="flex items-center gap-2">
          {/* Points are only worth showing when a question can be split — on a single-answer
              question "1 / 1" is noise the score line already covers. */}
          {item.max_points > 1 && (
            <span className="font-mono text-[11px] text-muted">
              {item.points} / {item.max_points}
            </span>
          )}
          {item.is_correct === null ? (
            <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted">{t('exams.resultItem.notAnswered')}</span>
          ) : item.is_correct ? (
            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent">{t('exams.resultItem.correct')}</span>
          ) : partial ? (
            <span className="rounded-full bg-warn/15 px-2 py-0.5 text-[11px] font-medium text-warn">{t('exams.resultItem.partlyCorrect')}</span>
          ) : (
            <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-medium text-danger">{t('exams.resultItem.incorrect')}</span>
          )}
        </div>
      </div>

      {item.clip ? (
        <div className="mt-2">
          {item.clip.playback_url ? (
            <VideoPlayer src={item.clip.playback_url} poster={item.clip.poster_url ?? undefined} className="w-full" />
          ) : (
            <div className="grid h-40 place-items-center rounded border border-dashed border-line-2 bg-bg-2 text-sm text-muted">
              {t('exams.runner.clipUnavailable')}
            </div>
          )}
          <p className="mt-2 text-[14px] font-medium text-ink">{t('exams.runner.whatsCorrectCall')}</p>
        </div>
      ) : (
        <>
          {item.rule_article && (
            <div className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-line-2 bg-surface-2 px-2.5 py-0.5 font-mono text-[10.5px] text-ink-2">
              <Icon.doc className="h-3 w-3" />{' '}
              {t('exams.runner.ruleWithTitle', { number: item.rule_article.number, title: item.rule_article.title })}
            </div>
          )}
          <p className="mt-1.5 text-[14px] font-medium text-ink">{item.question_text || t('exams.runner.whatsCorrectCall')}</p>
        </>
      )}

      <div className="mt-3 flex flex-col gap-1.5">
        {item.options.map((opt, i) => {
          const isCorrect = item.correct_answers.includes(opt);
          const isPicked = item.selected_answers.includes(opt);
          // Four states. The ring marks what the referee chose (right or wrong), the fill marks
          // what was actually correct — so a missed correct answer still stands out as green
          // without being mistaken for something they picked.
          const cls = isCorrect
            ? isPicked
              ? 'border-accent bg-accent/15 text-ink ring-1 ring-accent'
              : 'border-accent/60 bg-accent/10 text-ink'
            : isPicked
              ? 'border-danger bg-danger/10 text-ink ring-1 ring-danger'
              : 'border-line bg-surface text-ink-2';
          const label = isCorrect
            ? isPicked
              ? voice === 'self'
                ? t('exams.resultItem.yourAnswerCorrect')
                : t('exams.resultItem.theirAnswerCorrect')
              : t('exams.resultItem.missed')
            : isPicked
              ? voice === 'self'
                ? t('exams.resultItem.yourAnswerWrong')
                : t('exams.resultItem.theirAnswerWrong')
              : null;
          return (
            <div key={opt} className={'flex items-start justify-between gap-3 rounded-btn border-[1.5px] px-3.5 py-2 text-[13px] ' + cls}>
              <span className="flex-1">
                <span className="font-semibold text-ink-3">{OPTION_LETTERS[i]}）</span> {opt}
              </span>
              <span className="flex flex-none items-center gap-2 text-[11px]">
                {label && <span className="text-muted">{label}</span>}
                {isCorrect && <Icon.check className="h-3.5 w-3.5 text-accent" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
